import React from 'react';
import Box from '@amzn/meridian/box';
import Checkbox from '@amzn/meridian/checkbox';
import Icon from '@amzn/meridian/icon';
import Text from '@amzn/meridian/text';
import checkSmallTokens from '@amzn/meridian-tokens/base/icon/check-small';
import { AssignmentAssociate } from '../models';
import { getInitials } from '../../utils';

export interface IndirectAssociateRowProps {
  associate: AssignmentAssociate;
  processPathId: string;
  onRemove: (associateId: string, processPathId: string) => void;
}

/**
 * Flat horizontal row representing a single associate assigned to an
 * indirect process path (e.g. Problem solve). Indirect paths do not use
 * workstation cards, so associates are rendered as a row of these chips
 * directly under the section header.
 *
 * The checkbox starts checked (the associate is currently assigned); when
 * the user unchecks it, `onRemove` fires with the associate id and the
 * owning process path id so the page can remove them from the indirect
 * path. The trailing checkmark Icon is a visual affordance matching the
 * design mock and is independent of the checkbox state.
 */
const IndirectAssociateRow: React.FC<IndirectAssociateRowProps> = ({
  associate,
  processPathId,
  onRemove,
}) => {
  const initials = getInitials(associate.associateName);

  const handleChange = (nextChecked: boolean) => {
    // Only react on transition from checked → unchecked; per the design,
    // a re-check would require picking a target location via a different
    // flow and is not handled here.
    if (!nextChecked) {
      onRemove(associate.associateId, processPathId);
    }
  };

  return (
    <Box className="assignments-indirect-associate-row" spacingInset="200">
      <Checkbox checked={true} onChange={handleChange}>
        <Box className="assignments-indirect-associate-row__avatar">
          <Text type="b200">{initials}</Text>
        </Box>
        <Text type="b300">{associate.associateName}</Text>
        <Icon tokens={checkSmallTokens} />
      </Checkbox>
    </Box>
  );
};

export default IndirectAssociateRow;
